<template>
  <button
    :type="type"
    :disabled="disabled"
    @click="click"
    class="flex cursor-pointer items-center justify-center rounded-md border font-medium leading-5 disabled:cursor-not-allowed"
    :class="buttonClasses"
  >
    <ButtonSpinner
      v-if="loading"
      size="xs"
      :variant="spinnerVariant"
    />
    <component
      :is="icon"
      v-if="!loading && icon"
      :class="[
        'h-[18px] w-[18px]',
      ]"
    />
    <span
      v-if="hasContent || loading || withLoading"
      class="transition-[padding] duration-200"
      :class="[{ 'pl-1': withLoading }, { 'pl-2': loading || icon }]"
    >
      <span v-if="loading">{{ loadingText }}</span>
      <slot v-if="!loading" />
    </span>
  </button>
</template>

<script setup>
import { computed, useSlots } from 'vue';
import ButtonSpinner from './ButtonSpinner.vue';
import {
  buttonSizes,
  buttonVariants,
  BUTTON_SIZE,
  BUTTON_SIZE_CLASS_MAP,
  BUTTON_VARIANT,
  BUTTON_VARIANT_CLASS_MAP,
  getButtonSpinnerVariant,
} from './utils.js';

const slots = useSlots();
const emits = defineEmits(['click']);

const hasContent = computed(() => !!slots.default);

const props = defineProps({
  type: {
    type: String,
    default: 'button',
    validator: (value) => ['button', 'submit', 'reset'].includes(value),
  },
  size: {
    type: String,
    default: BUTTON_SIZE.md,
    validator: (size) => buttonSizes.includes(size),
  },
  variant: {
    type: String,
    default: BUTTON_VARIANT.main,
    validator: (variant) => buttonVariants.includes(variant),
  },
  icon: {
    type: String,
  },
  loading: {
    type: Boolean,
    default: null,
  },
  loadingText: {
    type: String,
    default: 'Processing...',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});

const spinnerVariant = computed(() => getButtonSpinnerVariant(props.variant));
const withLoading = computed(() => typeof props.loading === 'boolean');
const buttonClasses = computed(() => [
  BUTTON_VARIANT_CLASS_MAP[props.variant],
  BUTTON_SIZE_CLASS_MAP[props.size]?.[withLoading.value ? 'loading' : 'default'],
]);

function click($event) {
  if (props.disabled) return;
  if (props.loading) return;
  emits('click', $event);
}
</script>
